import * as React from 'react'
import classNames from 'classnames'
import { KebabMenu, KebabMenuOption } from 'uiComponents/menus/kebabMenu'
import { quote } from 'capacity/copy'
import { RowStatus } from 'capacity/staging/capacityDraft'
import { useTranslation } from 'translations'

const COPIED_VISIBLE_MS = 1500

export async function copyText(text: string): Promise<boolean> {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text)
            return true
        } catch {}
    }
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.className = 'capacity-copy-buffer'
    document.body.appendChild(textarea)
    textarea.select()
    let copied = false
    try {
        copied = document.execCommand('copy')
    } catch {
        copied = false
    }
    document.body.removeChild(textarea)
    return copied
}

interface ProductRowMenuProps {
    productName: string
    status: RowStatus
    onToggle?: () => void
    viewLink: string
    isOptionItem: boolean
    timeSlotsLink?: string
    numericId: number | undefined
    numericIdLoading: boolean
}

export function ProductRowMenu({
    productName,
    status,
    onToggle,
    viewLink,
    isOptionItem,
    timeSlotsLink,
    numericId,
    numericIdLoading,
}: ProductRowMenuProps) {
    const { t } = useTranslation()
    const [copyBubble, setCopyBubble] = React.useState({
        visible: false,
        text: t('capacity.products.menu.copied'),
        shown: 0,
    })

    React.useEffect(() => {
        if (!copyBubble.visible) {
            return undefined
        }
        const timer = window.setTimeout(
            () => setCopyBubble((current) => ({ ...current, visible: false })),
            COPIED_VISIBLE_MS,
        )
        return () => window.clearTimeout(timer)
    }, [copyBubble.visible, copyBubble.shown])

    const showCopyResult = (copied: boolean) =>
        setCopyBubble((current) => ({
            visible: true,
            text: copied ? t('capacity.products.menu.copied') : t('capacity.products.menu.copyFailed'),
            shown: current.shown + 1,
        }))

    const options: KebabMenuOption[] = []
    if (onToggle) {
        if (status === 'removing') {
            options.push({ label: t('capacity.products.menu.undoRemove'), icon: 'Undo2', onClick: onToggle })
        } else if (status === 'new') {
            options.push({ label: t('capacity.products.menu.dontAdd'), icon: 'X', onClick: onToggle })
        } else {
            options.push({ label: t('capacity.products.menu.remove'), icon: 'Trash2', onClick: onToggle })
        }
    }
    options.push({
        label: isOptionItem ? t('capacity.products.menu.viewOptionItem') : t('capacity.products.menu.viewProduct'),
        icon: 'Eye',
        link: viewLink,
    })
    if (timeSlotsLink) {
        options.push({ label: t('capacity.products.menu.viewTimeSlots'), icon: 'Clock', link: timeSlotsLink })
    }
    if (!isOptionItem) {
        if (numericIdLoading || numericId === undefined) {
            options.push({
                label: t('capacity.products.menu.productIdLoading'),
                icon: 'Copy',
                disabled: true,
                disabledReason: t('capacity.products.menu.productIdLoadingReason'),
            })
        } else {
            const text = String(numericId)
            options.push({
                label: t('capacity.products.menu.productId', { id: text }),
                icon: 'Copy',
                onClick: () => {
                    copyText(text).then(showCopyResult)
                },
            })
        }
    }

    return (
        <span className="capacity-row-menu">
            <KebabMenu
                options={options}
                listAnchor="right"
                triggerLabel={t('capacity.products.menu.more', { name: quote(productName) })}
                className="capacity-row-kebab"
            />
            <span
                className={classNames('capacity-row-copied', { visible: copyBubble.visible })}
                role="status"
                aria-live="polite"
            >
                {copyBubble.text}
            </span>
        </span>
    )
}
